import React from 'react';
import { DollarSign, PiggyBank, Calendar, Shield, LogIn, HelpCircle, CreditCard, Plane, Snowflake, BookOpen, Repeat, TrendingUp, Bell, Zap, BarChart, Split, CheckCircle, AlertCircle, MessageSquare, XCircle, Wallet } from 'lucide-react';
import { signIn, createUser } from '../lib/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import TermsOfService from './TermsOfService';

export default function LandingPage() {
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [showContactModal, setShowContactModal] = React.useState(false);
  const [isLogin, setIsLogin] = React.useState(true);
  const [showTerms, setShowTerms] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
    acceptTerms: false
  });
  const [contactForm, setContactForm] = React.useState({
    email: '',
    subject: '',
    message: ''
  });
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please try again.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please sign up.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please sign in.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (isLogin) {
        await signIn(formData.email, formData.password);
      } else {
        if (!formData.acceptTerms) {
          setError('You must accept the Terms of Service to create an account.');
          return;
        }
        await createUser(formData.email, formData.password);
      }
    } catch (err: any) {
      const errorCode = err?.code || 'unknown';
      setError(getErrorMessage(errorCode));
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await addDoc(collection(db, 'supportTickets'), {
        userEmail: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
        status: 'open',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        responses: []
      });

      setSuccess('Message sent successfully! We will get back to you soon.');
      setContactForm({ email: '', subject: '', message: '' });
      setShowContactModal(false);
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', acceptTerms: false });
    setError(null);
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  const features = [
    {
      icon: Calendar,
      title: "Smart Calendar",
      description: "Track bills, income, and expenses with our intelligent financial calendar. Supports recurring transactions and automatic bill scheduling."
    },
    {
      icon: Wallet,
      title: "Budget Buckets",
      description: "Organize spending into categories like groceries, fuel, and dining. Track expenses on-the-go and manage your daily spending with our digital envelope system."
    },
    {
      icon: Snowflake,
      title: "Debt Snowball",
      description: "Strategically eliminate debt with our snowball calculator. Automatically rolls payments into next debt when one is paid off."
    },
    {
      icon: CreditCard,
      title: "Subscription Manager",
      description: "Track and manage your recurring subscriptions. Get insights into your monthly subscription costs and never miss a payment."
    },
    {
      icon: Split,
      title: "Split Transactions",
      description: "Split large expenses across multiple paydays automatically. Perfect for managing big purchases without straining your budget."
    },
    {
      icon: TrendingUp,
      title: "Smart Balance",
      description: "Real-time running balance calculations and intelligent bill rescheduling to prevent overdrafts. Always know your true financial position."
    },
    {
      icon: Plane,
      title: "Vacation Planner",
      description: "Plan and save for your dream vacations. Split costs across multiple paydays and track your savings progress."
    },
    {
      icon: BarChart,
      title: "Financial Overview",
      description: "Get a clear picture of your monthly income, expenses, and savings. Track your net worth and financial progress over time."
    },
    {
      icon: Repeat,
      title: "Recurring Transactions",
      description: "Set up weekly, bi-weekly, monthly, or yearly recurring transactions for both income and expenses. Never miss a bill again."
    },
    {
      icon: Zap,
      title: "Auto-Payments",
      description: "Track automatic payments and get a clear view of your automated financial commitments. Stay on top of your recurring bills."
    },
    {
      icon: Bell,
      title: "Smart Notifications",
      description: "Get important updates about your finances, upcoming bills, and account status. Stay informed about your financial health."
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your data is protected with enterprise-grade security. We do not collect any banking information or sensitive financial data."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700">
      {/* Navigation */}
      <nav className="bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://cdn.buymeacoffee.com/uploads/profile_pictures/2024/11/7GWCqqRG5XR4Olys.png@300w_0e.webp"
                alt="ClearCash Logo"
                className="h-8 w-8 rounded-full"
              />
              <span className="ml-2 text-xl font-bold text-white">ClearCash</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowContactModal(true)}
                className="text-white hover:text-indigo-100 transition-colors"
              >
                Contact Us
              </button>
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-16 pb-8 text-center">
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl md:text-6xl">
            Take Control of Your Financial Future
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-indigo-100 sm:text-lg md:mt-5 md:text-xl">
            Smart budgeting, automated savings, and intelligent debt management all in one place
          </p>
          <div className="mt-8">
            <button
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 md:text-lg"
            >
              Get Started
            </button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 pb-16">
          {features.map((feature, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
              <feature.icon className="h-8 w-8 text-indigo-200 mb-4" />
              <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
              <p className="mt-2 text-indigo-100">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {isLogin ? 'Sign in to your account' : 'Create a new account'}
              </h2>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              {!isLogin && (
                <div className="flex items-center">
                  <input
                    id="acceptTerms"
                    name="acceptTerms"
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900">
                    I accept the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      Terms of Service
                    </button>
                  </label>
                </div>
              )}

              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {isLogin ? 'Sign in' : 'Create account'}
              </button>

              <div className="text-sm text-center">
                <button
                  type="button"
                  onClick={toggleAuthMode}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              {(error || success) && (
                <div className={`rounded-md ${error ? 'bg-red-50' : 'bg-green-50'} p-4`}>
                  <div className="flex">
                    <AlertCircle className={`h-5 w-5 ${error ? 'text-red-400' : 'text-green-400'}`} />
                    <div className="ml-3">
                      <h3 className={`text-sm font-medium ${error ? 'text-red-800' : 'text-green-800'}`}>
                        {error || success}
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  id="subject"
                  type="text"
                  required
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  required
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      )}

      {showTerms && (
        <TermsOfService
          onClose={() => setShowTerms(false)}
          onAccept={() => {
            setFormData(prev => ({ ...prev, acceptTerms: true }));
            setShowTerms(false);
          }}
        />
      )}
    </div>
  );
}